import React, { useState, useEffect } from 'react';
import './AnnotationDialog.css';

interface AnnotationDialogProps {
    isOpen: boolean;
    selectedText: string;
    selectedHtml?: string; // HTML content of selected text for display
    onClose: () => void;
    onSave: (elaborated: string) => void;
    onRemove?: () => void; // For removing annotations
    existingElaborated?: string; // For editing existing annotations
}

const AnnotationDialog: React.FC<AnnotationDialogProps> = ({
    isOpen,
    selectedText,
    selectedHtml,
    onClose,
    onSave,
    onRemove,
    existingElaborated = '',
}) => {
    const [elaborated, setElaborated] = useState(existingElaborated);

    // Reset elaborated text when dialog opens/closes or selected text changes
    useEffect(() => {
        if (isOpen) {
            setElaborated(existingElaborated);
        } else {
            setElaborated('');
        }
    }, [isOpen, existingElaborated]);

    const handleSave = () => {
        if (elaborated.trim()) {
            onSave(elaborated.trim());
            setElaborated('');
            onClose();
        }
    };

    const handleCancel = () => {
        setElaborated('');
        onClose();
    };

    const handleRemove = () => {
        if (onRemove) {
            onRemove();
            setElaborated('');
            onClose();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            handleCancel();
        } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            handleSave();
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="annotation-dialog-overlay" onClick={handleCancel}>
            <div className="annotation-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="annotation-dialog-header">
                    <h3>{existingElaborated ? 'Edit Annotation' : 'Add Annotation'}</h3>
                    <button
                        className="annotation-dialog-close"
                        onClick={handleCancel}
                        title="Close (Esc)"
                    >
                        ×
                    </button>
                </div>
                
                <div className="annotation-dialog-content">
                    <div className="annotation-dialog-field">
                        <label className="annotation-dialog-label">
                            Selected Text (read-only)
                        </label>
                        <div
                            className="annotation-dialog-selected-content"
                            dangerouslySetInnerHTML={{ __html: selectedHtml || selectedText }}
                        />
                    </div>
                    
                    <div className="annotation-dialog-field">
                        <label className="annotation-dialog-label">
                            Elaborated Text <span className="annotation-dialog-required">*</span>
                        </label>
                        <textarea
                            value={elaborated}
                            onChange={(e) => setElaborated(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter the full explanation or expanded information..."
                            className="annotation-dialog-textarea"
                            rows={4}
                            autoFocus
                        />
                        <div className="annotation-dialog-hint">
                            This text will appear in a tooltip when hovering over the selected text.
                        </div>
                    </div>
                </div>
                
                <div className="annotation-dialog-footer">
                    {existingElaborated && onRemove && (
                        <button
                            className="annotation-dialog-button annotation-dialog-button-remove"
                            onClick={handleRemove}
                            title="Remove annotation"
                        >
                            Remove
                        </button>
                    )}
                    <button
                        className="annotation-dialog-button annotation-dialog-button-cancel"
                        onClick={handleCancel}
                    >
                        Cancel
                    </button>
                    <button
                        className="annotation-dialog-button annotation-dialog-button-save"
                        onClick={handleSave}
                        disabled={!elaborated.trim()}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AnnotationDialog;

